import { useAppDispatch } from "@/app/hooks";
import {
  createRedemption,
  getRedemptionDetails,
  getSubscriptions,
  updateRedemption,
} from "@/features";
import { OptionProps } from "@/types";
import { customStylesSelect, toastify } from "@/utils";
import { useFormik } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import * as Yup from "yup";

type Props = {
  isOpen: boolean;
  selectedId: number;
  onSuccess: () => void;
  toggle: () => void;
};

export const CreateOrEditRedemption = ({
  isOpen,
  selectedId,
  onSuccess,
  toggle,
}: Props) => {
  const dispatch = useAppDispatch();
  const [subscriptionOptions, setSubscriptionOptions] = useState<OptionProps[]>(
    [],
  );
  const fileRef = useRef<HTMLInputElement>(null);

  const formik = useFormik({
    initialValues: {
      promotion_name: "",
      start_date: "",
      end_date: "",
      price_id: "",
      package_id: null,
      auto_generate: false,
      number_of_codes: 0,
    },
    validationSchema: Yup.object().shape({
      promotion_name: Yup.string().required("Please fill out this field."),
      package_id: Yup.object().required("Please fill out this field."),
      start_date: Yup.string().required("Please fill out this field."),
      end_date: Yup.string().required("Please fill out this field."),
      number_of_codes: Yup.number().required("Please fill out this field."),
    }),
    enableReinitialize: true,
    onSubmit: async (values) => {
      const formatValues = {
        ...values,
        package_id: Number((values.package_id as any)?.value),
        id: selectedId,
      };

      try {
        if (selectedId) {
          await dispatch(updateRedemption(formatValues)).unwrap();
          toastify("Redemption has been updated successfully", "success");
        } else {
          await dispatch(createRedemption(formatValues)).unwrap();
          toastify("Redemption has been created successfully", "success");
        }

        onSuccess();
      } catch (error) {
        console.log("error: ", error);
        toastify("An error occurred. Please try again.", "error");
      } finally {
        toggle();
      }
    },
  });

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleSubmit,
    setFieldValue,
  } = formik;

  const getSubscriptionOptions = useCallback(async () => {
    try {
      const subscriptions = await dispatch(getSubscriptions()).unwrap();
      setSubscriptionOptions(
        subscriptions.map((item) => ({
          label: item.name,
          value: item.id?.toString() as string,
        })),
      );
    } catch (error: any) {
      console.log("error: ", error);
    }
  }, [dispatch]);

  const getRedemptionDetailsInfo = useCallback(
    async (id: number) => {
      try {
        const details = await dispatch(getRedemptionDetails(id)).unwrap();

        const findPackageId = subscriptionOptions.find(
          (item) => Number(item?.value) === details.package_id,
        );

        setFieldValue("promotion_name", details.promotion_name);
        setFieldValue("package_id", findPackageId || null);
        setFieldValue("start_date", details.created_at);
        setFieldValue("end_date", details.end_date);
        setFieldValue("auto_generate", false);
        setFieldValue("price_id", details.price_id);
        setFieldValue("number_of_codes", details.total_code);
      } catch (error: any) {
        console.log("error: ", error);
      }
    },
    [dispatch, setFieldValue, subscriptionOptions],
  );

  useEffect(() => {
    getSubscriptionOptions();
  }, [getSubscriptionOptions]);

  useEffect(() => {
    if (selectedId) {
      getRedemptionDetailsInfo(selectedId);
    }
  }, [getRedemptionDetailsInfo, selectedId]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <form onSubmit={handleSubmit}>
        <ModalHeader toggle={toggle}>
          {selectedId ? "Edit Redemption" : "Create Redemption"}
        </ModalHeader>
        <ModalBody>
          <Row className="g-3">
            <Col xs={12} lg={8}>
              <FormGroup>
                <Label className="required">Promotion Name</Label>
                <Input
                  name="promotion_name"
                  placeholder="Promotion Name"
                  value={values.promotion_name}
                  onChange={handleChange}
                  className={
                    errors.promotion_name && touched.promotion_name
                      ? "is-invalid"
                      : ""
                  }
                />
                {errors.promotion_name && touched.promotion_name && (
                  <p className="text-danger mt-2">{errors.promotion_name}</p>
                )}
              </FormGroup>
            </Col>
            <Col xs={12} lg={4}>
              <FormGroup>
                <Label className="required">Apply Subscription</Label>
                <Select
                  options={subscriptionOptions}
                  value={values.package_id}
                  placeholder="Select Subscription"
                  onChange={(item: OptionProps) =>
                    setFieldValue("package_id", item)
                  }
                  styles={customStylesSelect(
                    !!(errors.package_id && touched.package_id),
                  )}
                />
                {errors.package_id && touched.package_id && (
                  <p className="text-danger mt-2">{errors.package_id}</p>
                )}
              </FormGroup>
            </Col>
            <Col xs={12} lg={4}>
              <FormGroup>
                <Label className="required">Start Date</Label>
                <Flatpickr
                  name="start_date"
                  className={`form-control ${
                    errors.start_date && touched.start_date ? "is-invalid" : ""
                  }`}
                  options={{
                    dateFormat: "d/m/Y",
                  }}
                  placeholder="Start Date"
                  value={values.start_date}
                  onChange={(_, date) => {
                    setFieldValue("start_date", date);
                  }}
                />
              </FormGroup>
            </Col>
            <Col xs={12} lg={4}>
              <FormGroup>
                <Label className="required">Finish Date</Label>
                <Flatpickr
                  name="end_date"
                  className={`form-control ${
                    errors.end_date && touched.end_date ? "is-invalid" : ""
                  }`}
                  options={{
                    dateFormat: "d/m/Y",
                  }}
                  placeholder="Finish Date"
                  value={values.end_date}
                  onChange={(_, date) => {
                    setFieldValue("end_date", date);
                  }}
                />
              </FormGroup>
            </Col>
            <Col xs={12} lg={4}>
              <FormGroup>
                <Label>Stripe Price ID</Label>
                <Input
                  name="price_id"
                  placeholder="Stripe Price ID"
                  value={values.price_id}
                  onChange={handleChange}
                />
              </FormGroup>
            </Col>
            <Col xs={12} lg={4}>
              <FormGroup>
                <Label>Import File</Label>
                <Button
                  color="primary"
                  className="w-100"
                  onClick={() => fileRef.current?.click()}
                >
                  <i className="ri-upload-cloud-2-line me-1" />
                  Select File
                </Button>
                <input
                  type="file"
                  className="d-none"
                  id="file"
                  accept=".csv"
                  ref={fileRef}
                />
              </FormGroup>
            </Col>
            <Col xs={12} lg={4} className="d-flex align-items-center">
              <Label className="mt-3">
                <Input
                  type="checkbox"
                  className="me-2"
                  defaultChecked={values.auto_generate}
                  onChange={() => {
                    setFieldValue("auto_generate", !values.auto_generate);
                  }}
                />
                Auto generate codes
              </Label>
            </Col>
            <Col xs={12} lg={4}>
              <FormGroup>
                <Label className="required">Number Of Codes</Label>
                <Input
                  type="number"
                  min={0}
                  name="number_of_codes"
                  value={values.number_of_codes}
                  onChange={handleChange}
                  className={
                    errors.number_of_codes && touched.number_of_codes
                      ? "is-invalid"
                      : ""
                  }
                />
                {errors.number_of_codes && touched.number_of_codes && (
                  <p className="text-danger mt-2">{errors.number_of_codes}</p>
                )}
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={toggle} disabled={isSubmitting}>
            Close
          </Button>
          <Button type="submit" color="primary" disabled={isSubmitting}>
            {selectedId ? "Update" : "Create"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
